import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Alert, Tooltip, Popper, Paper } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import useApi from "../../hooks/useApi";
import { verifyOtp, createAndSendOtp } from "../../services/userService";
import { HrEmployeeOtpDto, OtpResponseDto } from "../../types/User";
import { IconButton, Dialog, DialogTitle } from "@mui/material";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import CloseIcon from "@mui/icons-material/Close";
import { USERSIGNUPMESSAGES } from "../../constants/messages";
import { obfuscateEmail, obfuscatePhone } from "../../utils/maskUtils";
import {
  OTPFOR,
  OTPMODE,
  OTPSENTMODE,
  USERINFO,
  OTPSTATUS,
  GLOBALSETTINGS,
  SYSTEMSETTINGS,
  COMMONCONSTANTS,
  VISIBLE_DIGITS,
  VERIFICATIONTYPE,
} from "../../data/constants";
import { USERSIGNINMESSAGES, POPOVERMESSAGES } from "../../constants/messages";
import { HelpOutline } from "@mui/icons-material";
import { useSettingValue } from "../../services/useSettingValue";
import CommonResendOtpStatus from "../signin/CommonResendOtpStatus";
import OtpCountDown from "../CommonComponent/OtpCountDown";
import { userName } from "../../tests/data/sharedData";
import OtpInput from "../CommonComponent/CustomOTP";
interface SignUpWithOTPProps {
  onOtpVerified: (otp: string, isOtpMatched: boolean) => void;
  onParentAction: (source?: string) => Promise<boolean>;
  headerText: string;
  email: string;
  phone: string;
  userName: string;
  otpSendTo: string;
  onIsSessionLocked: () => void;
}

const SignUpWithOTP: React.FC<SignUpWithOTPProps> = ({
  onOtpVerified,
  onParentAction,
  headerText,
  email,
  phone,
  otpSendTo,
  onIsSessionLocked,
}) => {
  const [otpInput, setOtpInput] = useState("");
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [otpStatus, setOtpStatus] = useState({
    isVisibleSendOtp: true,
    isOtpSent: false,
    timer: 30,
    isResendDisabled: true,
    otpErrorMessage: "",
    isVerified: false,
    otpSentTo: "",
    otpExpiryInSeconds: 0,
  });
  const updateOtpStatusState = useCallback((newState: Partial<typeof otpStatus>) => {
    setOtpStatus((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);
  const [openOTPDialog, setOpenOTPDialog] = React.useState(false);
  const [otpSentMode, setOtpSentMode] = useState("");
  const [isSessionLocked, setIsSessionLocked] = React.useState(false);
  const { loading, executeApi } = useApi();
  const otpRef = useRef(null);
  const otpFieldLength =
    Number(useSettingValue(SYSTEMSETTINGS.OTP_FIELD_LENGTH)) ||
    GLOBALSETTINGS.DEFAULT_OTP_FIELD_LENGTH;
  useEffect(() => {
    let countdown: ReturnType<typeof setTimeout>;
    if (otpStatus.timer > 0) {
      countdown = setTimeout(() => updateOtpStatusState({ timer: otpStatus.timer - 1 }), 1000);
    } else {
      updateOtpStatusState({ isResendDisabled: false });
    }
    return () => clearTimeout(countdown);
  }, [otpStatus.timer, updateOtpStatusState]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft]);
  const createOtpData = (): HrEmployeeOtpDto => ({
    emailAddress: email,
    phone: phone,
    userName: userName,
    otpSendTo: otpSendTo,
    otpFor: OTPFOR.SIGNUP,
    sessionValue: localStorage.getItem(USERINFO.SESSIONVALUE),
    mode: OTPMODE.SEND,
  });
  const handleSendOtp = async () => {
    if (!(await onParentAction(VERIFICATIONTYPE.OTP))) return;
    clearOtp();
    const empAndOtpData: HrEmployeeOtpDto = createOtpData();
    const response = await executeApi<HrEmployeeOtpDto, OtpResponseDto>(
      createAndSendOtp,
      empAndOtpData,
    );
    if (response) {
      setOpenOTPDialog(true);
      setOtpSentMode(response.otpSentTo === OTPMODE.EMAIL ? OTPSENTMODE.EMAIL : OTPSENTMODE.PHONE);
      updateOtpStatusState({
        isOtpSent: true,
        otpSentTo: response.otpSentTo,
        otpExpiryInSeconds: response.verificationTimeOut,
      });
      setTimeLeft(response.verificationTimeOut);
      startTimer();
      updateOtpStatusState({ isVisibleSendOtp: false });
      setTimeout(() => {
        focusFirstTextField();
      }, 100);
    }
  };
  const startTimer = () => {
    updateOtpStatusState({ timer: 30, isResendDisabled: true });
  };
  const handleResendOTP = async () => {
    const empAndOtpData: HrEmployeeOtpDto = createOtpData();
    const response = await executeApi<HrEmployeeOtpDto, OtpResponseDto>(
      createAndSendOtp,
      empAndOtpData,
    );
    if (response) {
      startTimer();
      updateOtpStatusState({
        otpExpiryInSeconds: response.verificationTimeOut,
      });
      clearOtp();
      setTimeLeft(otpStatus.otpExpiryInSeconds);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpInput) {
      updateOtpStatusState({ otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_REQUIRED });
      focusFirstTextField();
    } else if (otpInput.length < otpFieldLength) {
      updateOtpStatusState({
        otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_6_DIGIT_NUMBER + " " + otpSentMode,
      });
      focusFirstTextField();
    } else {
      const empAndOtpData: HrEmployeeOtpDto = {
        sessionValue: localStorage.getItem(USERINFO.SESSIONVALUE),
        verificationCode: otpInput,
        mode: OTPMODE.VERIFY,
      };
      const response = await executeApi<HrEmployeeOtpDto, OtpResponseDto>(verifyOtp, empAndOtpData);
      if (response && response.result === OTPSTATUS.MATCHED) {
        updateOtpStatusState({ isVerified: true, isOtpSent: false });
        onOtpVerified(otpInput, true);
        setOpenOTPDialog(false);
      } else if (response.result === OTPSTATUS.EXPIRED) {
        updateOtpStatusState({
          otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_EXPIRED,
          isVerified: false,
        });
      } else if (response.result === OTPSTATUS.SESSION_LOCKED) {
        setIsSessionLocked(true);
        updateOtpStatusState({
          isVerified: false,
        });
        onIsSessionLocked();
      } else {
        updateOtpStatusState({
          otpErrorMessage: USERSIGNUPMESSAGES.OTP_CODE_DOES_NOT_MATCH,
          isVerified: false,
        });
      }
    }
  };
  const clearOtp = () => {
    setOtpInput("");
    otpRef.current?.clear();
    focusFirstTextField();
    updateOtpStatusState({ otpErrorMessage: "" });
  };

  const focusFirstTextField = () => {
    if (!otpRef.current?.isComplete()) {
      otpRef.current?.focusFirstEmpty();
      return;
    }
  };

  const handleCloseDialog = () => {
    setOpenOTPDialog(false);
  };
  // PopOver
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? COMMONCONSTANTS.SIMPLEPOPOVER : undefined;
  const handleClickOutsideHover = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (anchorEl) {
      document.addEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
    } else {
      document.removeEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
    }
    return () => {
      document.removeEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
    };
  }, [anchorEl]);

  const handleClickOutside = (event: MouseEvent) => {
    if (popperRef.current && !popperRef.current.contains(event.target as Node)) {
      setAnchorEl(null);
    }
  };

  const handleCustomOtpChange = (otp) => {
    setOtpInput(otp);
  };
  return (
    <>
      <BackdropProgressBar loading={loading} />
      <Typography component="h1" variant="h1" className="paper-title">
        {headerText} Sign up with OTP
        <div className="popper-wrapper">
          <Tooltip title={POPOVERMESSAGES.HelpTooltip} arrow placement="top">
            <IconButton
              id="SignUpHelpOutIcon"
              tabIndex={-1}
              aria-describedby={id}
              onClick={handleClick}
              className="help-icon-color"
            >
              {<HelpOutline className="help-outline-icon" />}
            </IconButton>
          </Tooltip>
          {/* Popover */}
          <Popper className="signup-popper" id={id} open={open} anchorEl={anchorEl} ref={popperRef}>
            <Paper sx={{ boxShadow: 3 }} className="signup-paper">
              {/* Arrow */}
              <div className="arrow" />
              <div dangerouslySetInnerHTML={{ __html: POPOVERMESSAGES.SIGNUP_WITH_OTP }} />
              <div className="popover-btn">
                {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                <button onClick={handleClickOutsideHover} autoFocus className="button">
                  {COMMONCONSTANTS.GOTIT}
                </button>
              </div>
            </Paper>
          </Popper>
        </div>
      </Typography>
      <Box textAlign="center">
        <Button type="button" variant="contained" fullWidth onClick={handleSendOtp}>
          {headerText === "And" ? "Send OTP and Proceed with Sign Up" : "Send OTP"}
        </Button>
      </Box>
      <Dialog
        onClose={handleCloseDialog}
        aria-labelledby="customized-dialog-title"
        open={openOTPDialog}
        maxWidth="sm"
        PaperProps={{ sx: { width: 480, height: 420 } }}
      >
        <DialogTitle
          id="customized-dialog-title"
          sx={{ fontWeight: "bold", textAlign: "center" }}
          className="dialogtitle-font-size"
        >
          OTP Verification for {otpSentMode}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "#fff",
          }}
        >
          <CloseIcon />
        </IconButton>
        {otpStatus.isOtpSent && (
          <>
            <BackdropProgressBar loading={loading} />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={otpStatus.otpSentTo === OTPMODE.EMAIL ? "/otp-email.gif" : "/otp-mobile.gif"}
                alt="description"
                style={{ width: "100px", height: "100px" }}
              />
            </div>
            {isSessionLocked && (
              <Alert severity="error" sx={{ marginBottom: "10px" }} className="alert-alert">
                {USERSIGNINMESSAGES.SESSION_LOCKED_MESSAGE.replace(
                  "{Session_value}",
                  localStorage.getItem(USERINFO.SESSIONVALUE),
                )}
              </Alert>
            )}
            <div className={isSessionLocked ? "disabled-div" : ""}>
              <Alert severity="error" icon={false} className="alert-alert masked" sx={{ mx: 2 }}>
                We have sent you <strong>One Time Password</strong> to your{" "}
                {otpSentMode === OTPSENTMODE.EMAIL
                  ? `${otpSentMode} (${obfuscateEmail(email)})`
                  : `${otpSentMode} (${obfuscatePhone(phone, VISIBLE_DIGITS)})`}
              </Alert>

              <OtpCountDown timeLeft={timeLeft} />
              <div className="div-align">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    px: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                    sx={{
                      flexWrap: "wrap",
                      width: "100%",
                      maxWidth: {
                        xs: "100%",
                        sm: `${otpFieldLength * 63}px`,
                      },
                    }}
                  >
                    <OtpInput
                      length={otpFieldLength}
                      onChange={handleCustomOtpChange}
                      ref={otpRef}
                    />
                  </Box>
                </Box>
                <div>
                  <CommonResendOtpStatus
                    otpErrorMessage={otpStatus.otpErrorMessage}
                    timer={otpStatus.timer}
                    isResendDisabled={otpStatus.isResendDisabled}
                    handleResendOTP={handleResendOTP}
                  />
                </div>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={isSessionLocked}
                >
                  Verify And Sign Up
                </Button>
              </div>
            </div>
          </>
        )}
      </Dialog>
    </>
  );
};
export default SignUpWithOTP;
